import {
  Location,
  LocationsApiService,
  Office,
  OfficesApiService,
  OfficeStatistic,
  useDebounce,
  useService,
  WorkspacesCount,
} from '@innowise-group/core';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {combineLatest, switchMap, tap} from 'rxjs';
import {AddOfficeFormValues} from '../../components/office-form';
import {OfficesModalsFacadeService} from '../../services/offices-modals-facade';
import {isLocationMatchingSearch} from '../../utilities/location-matching-search.utility';

export type LocationStatisticType = {
  location: string;
  locationStatistic: WorkspacesCount;
  officesStatistic?: Array<{
    address: string;
    workspacesCount: WorkspacesCount;
    officeId: string;
    attendancePercentage: number;
  }>;
};

export interface UseOfficesOverviewResult {
  countries: Array<{country: string; officesCount: number}>;
  selectedCountry: string;
  setSelectedCountry: (country: string) => void;
  officesLocations: Array<{
    location: string;
    offices: Office[];
    locationId: string;
  }>;
  handleOfficeClick: (officeId: string) => void;
  handleAddNewLocation: () => void;
  handleDeleteLocation: (id: string) => void;
  handleAddNewOffice: (initialValues?: Partial<AddOfficeFormValues>) => void;
  handleEditOffice: (id: string) => void;
  handleDeleteOffice: (id: string) => void;
  isLoading: boolean;
  isStatisticLoading: boolean;
  officesLocationsStatistic: Array<LocationStatisticType>;
  handleSearch: (event: React.FormEvent<HTMLOrSVGElement & HTMLInputElement>) => void;
  handleDeleteFloor: (handle: () => void) => void;
}

export function useOfficesOverview(): UseOfficesOverviewResult {
  const navigate = useNavigate();
  const locationsApi = useService(LocationsApiService);
  const officesApi = useService(OfficesApiService);
  const officesModalsFacade = useService(OfficesModalsFacadeService);

  const {t} = useTranslation();

  const [locations, setLocations] = useState<Location[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(t('pages.offices.allOffices'));
  const [overallStatisticOffices, setOverallStatisticOffices] = useState<OfficeStatistic[]>([]);
  const [search, setSearch] = useState('');

  const debouncedValue = useDebounce(search, 500);

  useEffect(() => {
    setSelectedCountry(t('pages.offices.allOffices'));
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    const subscriber = combineLatest([locationsApi.getLocations(), officesApi.getOffices()]).subscribe(
      ([fetchedLocations, fetchedOffices]) => {
        setLocations(fetchedLocations);
        setOffices(fetchedOffices);
        setIsLoading(false);
      },
    );
    return () => subscriber.unsubscribe();
  }, [locationsApi, officesApi]);

  useEffect(() => {
    const subscriber = officesApi.getOverallStatisticOffices().subscribe(setOverallStatisticOffices);
    return () => subscriber.unsubscribe();
  }, [officesApi, offices]);

  const countries = useMemo(() => {
    const locationsCountryMap = locations.reduce<Record<string, string[]>>(
      (accumulator, {countryName, id}) => ({
        ...accumulator,
        [countryName]: (accumulator[countryName] || []).concat([id]),
      }),
      {},
    );
    const countries = Object.entries(locationsCountryMap)
      .map(([country, locations]) => ({
        country,
        officesCount: offices.filter((office) => locations.includes(office.locationId)).length,
      }))
      .sort((country1, country2) => country1?.country?.localeCompare(country2.country));

    return [{country: t('pages.offices.allOffices'), officesCount: offices.length}, ...countries];
  }, [t, locations, offices]);

  useEffect(() => {
    if (!debouncedValue) {
      setSelectedCountry(t('pages.offices.allOffices'));
    } else {
      setSelectedCountry((value) => {
        return locations.find((location) => isLocationMatchingSearch(location, debouncedValue))?.countryName;
      });
    }
  }, [debouncedValue, locations, setSelectedCountry, t]);

  const officesLocations = useMemo(
    () =>
      Object.entries(
        locations
          .filter((location) => isLocationMatchingSearch(location, debouncedValue))
          .filter((location) => location.countryName === selectedCountry)
          .reduce<{[city: string]: Office[]}>((accumulator, location) => {
            return {
              ...accumulator,
              [location.id]: offices.filter((office) => office.locationId === location.id),
            };
          }, {}),
      ).map(([locationId, offices]) => {
        const locationById = locations.find((item) => item.id === locationId);
        return {location: locationById ? locationById.city : '', offices, locationId};
      }),
    [locations, offices, selectedCountry, debouncedValue],
  );

  const officesLocationsStatistic = useMemo(() => {
    const overallStatistic = {
      location: t('pages.offices.totalInfo'),
      locationStatistic: {},
    };
    (overallStatisticOffices || []).forEach(({workspacesCount}) => {
      Object.entries(workspacesCount).forEach(([key, value]) => {
        if (!overallStatistic.locationStatistic[key]) {
          overallStatistic.locationStatistic[key] = value;
        } else {
          overallStatistic.locationStatistic[key] += value;
        }
      });
    });
    const locationsStatistic = (overallStatisticOffices || []).map(({id, workspacesCount}) => {
      const locationId = offices.length && offices.find((office) => office.id === id)?.locationId;
      const officeAddress = offices.length && offices.find((office) => office.id === id)?.address;
      const attendancePercentage = offices.length && offices.find((office) => office.id === id)?.attendance_percentage;
      const country = locations.length && locations.find((location) => location.id === locationId)?.countryName;
      const city = locations.length && locations.find((location) => location.id === locationId)?.city;
      const locationStatistic = {
        location: country,
        locationStatistic: {} as WorkspacesCount,
        officesStatistic: [{address: `${city}, ${officeAddress}`, workspacesCount, officeId: id, attendancePercentage}],
      };
      Object.entries(workspacesCount).forEach(([key, value]) => {
        if (!locationStatistic.locationStatistic[key]) {
          locationStatistic.locationStatistic[key] = value;
        } else {
          locationStatistic.locationStatistic[key] += value;
        }
      });
      return locationStatistic;
    }) as Array<LocationStatisticType>;
    const overallLocationStatistic: Array<LocationStatisticType> = Object.values(
      locationsStatistic.reduce((accumulator, location) => {
        if (!accumulator[location.location]) {
          accumulator[location.location] = Object.assign({}, location);
        } else {
          Object.keys(location.locationStatistic).forEach((key) => {
            accumulator[location.location].locationStatistic[key] += location.locationStatistic[key];
          });
          accumulator[location.location].officesStatistic.push(...location.officesStatistic);
        }
        return accumulator;
      }, {}),
    );
    const sortedOverallLocationStatistic = overallLocationStatistic
      .map((obj) => ({
        ...obj,
        officesStatistic: obj.officesStatistic.sort((office1, office2) =>
          office1?.address?.localeCompare(office2.address),
        ),
      }))
      .sort((country1, country2) => country1?.location?.localeCompare(country2.location));

    return [overallStatistic, ...sortedOverallLocationStatistic] as Array<LocationStatisticType>;
  }, [t, overallStatisticOffices, offices, locations]);

  const handleSearch = (event: React.FormEvent<HTMLOrSVGElement & HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  return {
    countries,
    selectedCountry,
    setSelectedCountry,
    officesLocations,
    isLoading,
    isStatisticLoading: !overallStatisticOffices.length,
    officesLocationsStatistic,
    handleOfficeClick: (id) => {
      navigate(`/offices/${id}`);
    },
    handleAddNewLocation: () => {
      officesModalsFacade.openAddLocationModal((values) => {
        return locationsApi.addLocation(values).pipe(
          tap((location: Location) => {
            setLocations((values) => [...values, location]);
          }),
        );
      }, t('modals.addNewLocation'));
    },
    handleDeleteLocation: (id: string) => {
      officesModalsFacade
        .openDeleteLocationModal(
          t('modals.deleteCityTitle'),
          t('modals.deleteCityText'),
          t('buttons.delete'),
          t('buttons.cancel'),
        )
        .pipe(switchMap(() => locationsApi.deleteLocation(id)))
        .subscribe(() => {
          setLocations((currentLocations) => currentLocations.filter((location) => location.id !== id));
          setOffices((currentOffices) => currentOffices.filter((office) => office.locationId !== id));
        });
    },
    handleAddNewOffice: (initialValues?: Partial<AddOfficeFormValues>) => {
      officesModalsFacade.openAddOfficeModal(
        (values: AddOfficeFormValues) => {
          return officesApi.addOffice(values).pipe(
            tap((office: Office) => {
              return setOffices((values) => [...values, office]);
            }),
          );
        },
        initialValues,
        {title: t('modals.addNewOffice')},
        offices,
      );
    },
    handleEditOffice: (id: string) => {
      officesModalsFacade.openEditOfficeModal(
        id,
        (office) => {
          setOffices((currentOffices) => {
            const index = currentOffices.findIndex((o) => o.id === office.id);
            const updated = [...currentOffices];
            updated.splice(index, 1, office);
            return updated;
          });
        },
        {title: t('modals.editOffice')},
      );
    },
    handleDeleteOffice: (id: string) => {
      officesModalsFacade
        .openDeleteOfficeModal(
          t('modals.deleteOfficeTitle'),
          t('modals.deleteOfficeText'),
          t('buttons.delete'),
          t('buttons.cancel'),
        )
        .pipe(switchMap(() => officesApi.deleteOffice(id)))
        .subscribe(() => {
          setOffices((currentOffices) => currentOffices.filter((office) => office.id !== id));
        });
    },
    handleSearch,
    handleDeleteFloor: (handle) => {
      officesModalsFacade
        .openDeleteFloorModal(
          t('modals.deleteFloorTitle'),
          t('modals.deleteFloorText'),
          t('buttons.delete'),
          t('buttons.cancel'),
        )
        .subscribe(() => {
          handle();
        });
    },
  };
}
